import { useEffect, useState } from 'react';
import { elapsedSeconds } from '@/utils/duration';

/**
 * Elapsed seconds, always DERIVED from the stored start timestamp.
 *
 * The interval only triggers a re-render; it never accumulates. That is what
 * keeps the timer correct across a locked screen, a backgrounded tab or a
 * throttled/suspended PWA. The visibility listener refreshes immediately on
 * resume rather than waiting for the next tick.
 */
export function useSurveyTimer(
  startTimestamp: number | undefined,
  endTimestamp: number | undefined,
): number {
  const [seconds, setSeconds] = useState(() => elapsedSeconds(startTimestamp, endTimestamp));

  useEffect(() => {
    const update = () => setSeconds(elapsedSeconds(startTimestamp, endTimestamp));
    update();

    // A finished survey has a fixed duration: no ticking needed.
    if (!startTimestamp || endTimestamp) return;

    const id = setInterval(update, 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') update();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', update);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', update);
    };
  }, [startTimestamp, endTimestamp]);

  return seconds;
}
